import { ArrowDropDown } from "@mui/icons-material";
import Comments from "@mui/icons-material/Comment";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { get } from "lodash";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SaveAndExitButton from "../../../common/js/components/SaveAndExitButton";
import { buildAddress } from "../utils/operations";
import constants from "./constants";

const StyledDeleteButton = styled(Button)({
    backgroundColor: red[500],
    color: "white",
    "&:hover": {
        backgroundColor: red[800],
    },
});

const StyledPageToolbarDiv = styled("div")(({ isStatusTrackerVisible }) => ({
    padding: "10px 0px",
    backgroundColor: "#fafafa",
    position: "sticky",
    top: isStatusTrackerVisible ? "105px" : "62px",
    zIndex: 2,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
}));

const StyledToolbarButtons = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "0.5vw",
    paddingTop: "2vh",
});

const StyledProgressDiv = styled("div")({
    textAlign: "center",
    width: "5em",
    display: "inline-block",
});

const StyledCommentsButton = styled(Button)({
    color: "black",
});

const ConsultingDealToolbar = (props) => {
    const [actionAnchorEl, setActionAnchorEl] = useState(null);

    const {
        confirmDialog,
        deleteDeal,
        displayDeleteDialog,
        elementsSaving,
        handleChooseDisplayDeleteDialog,
        handleSave,
        handleSaveAndExit,
        handleSubmit,
        isStatusTrackerVisible,
        numComments,
        toggleComments,
        toggleConfirm,
        userProfile,
        authenticatedUserProfile,
        voucher,
    } = props;

    const navigate = useNavigate();
    const brokerSubmitActions = {
        Draft: {
            buttonText: "Submit Deal",
            submit: () => {
                handleSubmitAndRedirect(true, constants.urls.broker_submit(voucher.voucherKey), true);
            },
            variant: "contained",
            confirm: false,
            url: constants.urls.broker_submit,
        },
        "Broker Action Required": {
            buttonText: "Submit",
            submit: () => {
                handleSubmitAndRedirect(true, constants.urls.broker_submit(voucher.voucherKey), true);
            },
            variant: "contained",
            confirm: false,
            url: constants.urls.broker_submit,
        },
    };

    const submitActions = {
        broker: {
            ...brokerSubmitActions,
        },
        debtEquity: {
            ...brokerSubmitActions,
        },
        evp: {
            ...brokerSubmitActions,
        },
        portlandBrokerAdmin: {
            ...brokerSubmitActions,
            "Portland Review Required": {
                buttonText: "Portland Review Required",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    Approve: constants.urls.portland_approval,
                },
            },
        },
        arizonaBrokerAdmin: {
            ...brokerSubmitActions,
            "AZ Review Required": {
                buttonText: "AZ Review Required",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    Approve: constants.urls.arizona_approval,
                },
            },
        },
        dataEntry: {
            "Needs Review": {
                buttonText: "Start",
                submit: () => {
                    handleSubmit(false, constants.urls.assign_voucher_for_review(voucher.voucherKey), true);
                },
                confirm: false,
                url: constants.urls.assign_voucher_for_review,
            },
            "In Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    "Final Review": constants.urls.approve_voucher,
                },
            },
            "Admin Action Required": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    "Final Review": constants.urls.approve_voucher,
                },
            },
        },
        dataAdmin: {
            "Final Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    "Admin Action Required": constants.urls.reassign_voucher_to_dataentry,
                    Approve: constants.urls.approve_voucher,
                },
            },
        },
        accounting: {
            "Awaiting Payment": {
                buttonText: "Ready For Payment",
                submit: () => {
                    handleSubmitAndRedirect(true, constants.urls.ready_for_payment(voucher.voucherKey), true);
                },
                variant: "contained",
                confirm: false,
                url: constants.urls.ready_for_payment,
            },
        },
    };

    const mainAction = get(submitActions, `${userProfile.role}.${voucher.voucherStatus}`, null);

    const confirm = (url) => {
        toggleConfirm(url(voucher.voucherKey));
    };

    const handleSubmitAndRedirect = async (save, callback, bypassConfirm) => {
        const result = await handleSubmit(save, callback, bypassConfirm);

        if (result.success) {
            determineRedirectRoute();
        }
    };
    const handleSaveAndRedirect = async () => {
        const result = await handleSaveAndExit();
        if (result.success) {
            determineRedirectRoute();
        }
    };

    const determineRedirectRoute = () => {
        const role = userProfile.role;
        role === "accounting" ? navigate("/vouchers") : navigate("/deals");
    };

    const handleCancel = () => {
        toggleConfirm(null);
    };

    const handleDeleteDeal = () => {
        deleteDeal(get(voucher, "deal.dealKey"));
        handleChooseDisplayDeleteDialog(false, get(voucher, "deal.dealKey"));
        navigate("/deals");
    };

    const dealKey = `${get(voucher, "deal.dealKey")}`;

    const deletePermission = {
        dataAdmin: ["Draft", "Awaiting Payment", "Awaiting Comp", "Ready For Accounting", "Closed"],
        dataEntry: ["Draft", "Awaiting Payment", "Awaiting Comp"],
        debtEquity: ["Draft", "Awaiting Payment"],
        propertyManagement: ["Draft", "Awaiting Payment"],
        broker: ["Draft", "Awaiting Payment"],
    };

    const hideDeleteButton = () => {
        const isVoucherBatched = voucher.approvedBy !== null && voucher.approvedDate !== null;
        if (
            (isVoucherBatched && authenticatedUserProfile.role === "dataAdmin") ||
            (!isVoucherBatched &&
                (userProfile.user === get(voucher, "auditCreatedBy") ||
                    userProfile.role === "dataAdmin" ||
                    userProfile.role === "dataEntry"))
        ) {
            return (
                deletePermission[userProfile.role] !== undefined &&
                deletePermission[userProfile.role].includes(voucher.voucherStatus)
            );
        }
    };

    return (
        <StyledPageToolbarDiv isStatusTrackerVisible={isStatusTrackerVisible}>
            <div>
                <h2 className="page-title">{`Consulting Deal ${get(voucher, "deal.dealKey")}`}</h2>
                <p className="page-subtitle">
                    {buildAddress(get(voucher, "deal.property.primaryAddress"))}
                    {get(voucher, "voucherKey", null) ? " | " : " "}
                    {get(voucher, "deal.propertyType")} {get(voucher, "deal.transactionType")}
                </p>
            </div>
            <div>
                <StyledToolbarButtons container alignItems="center" spacing={0}>
                    <div>
                        <StyledCommentsButton onClick={toggleComments} style={{ marginLeft: 0, paddingLeft: 0 }}>
                            <Comments /> Comments ({numComments})
                        </StyledCommentsButton>
                    </div>
                    {hideDeleteButton() && (
                        <div>
                            <StyledDeleteButton
                                variant="outlined"
                                onClick={() => handleChooseDisplayDeleteDialog(true, dealKey)}
                            >
                                Delete
                            </StyledDeleteButton>
                        </div>
                    )}
                    <div>
                        {elementsSaving.saveAndExit ? (
                            <StyledProgressDiv>
                                <CircularProgress size={20} color="primary" />
                            </StyledProgressDiv>
                        ) : (
                            <React.Fragment>
                                <SaveAndExitButton onSave={handleSave} onSaveAndExit={handleSaveAndRedirect} />
                            </React.Fragment>
                        )}
                    </div>
                    <div>
                        {elementsSaving.submit ? (
                            <StyledProgressDiv>
                                <CircularProgress size={20} color="primary" />
                            </StyledProgressDiv>
                        ) : mainAction && mainAction.options ? (
                            <React.Fragment>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={(event) => setActionAnchorEl(event.currentTarget)}
                                    endIcon={<ArrowDropDown />}
                                >
                                    {get(mainAction, "buttonText")}
                                </Button>
                                <Menu
                                    anchorEl={actionAnchorEl}
                                    open={Boolean(actionAnchorEl)}
                                    onClose={() => setActionAnchorEl(null)}
                                >
                                    {Object.keys(get(mainAction, "options", {})).map((x) => {
                                        let url = get(mainAction, `options.${x}`);
                                        return (
                                            <MenuItem
                                                key={x}
                                                onClick={() => {
                                                    confirm(url);
                                                    setActionAnchorEl(null);
                                                }}
                                            >
                                                {x}
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            </React.Fragment>
                        ) : mainAction !== null ? (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => confirm(get(mainAction, `url`))}
                            >
                                {mainAction.buttonText}
                            </Button>
                        ) : (
                            voucher && (
                                <Button disabled variant="contained" color="secondary">
                                    {voucher.voucherStatus}
                                </Button>
                            )
                        )}
                    </div>
                </StyledToolbarButtons>
            </div>
            <Dialog open={confirmDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Continue?</DialogTitle>
                <DialogContent>Are you sure you want to submit this status change?</DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => mainAction["submit"](confirmDialog.onSubmit)} color="primary">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={displayDeleteDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Delete Deal Request?</DialogTitle>
                <DialogContent>Are you sure you want to permanently delete this deal?</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => handleChooseDisplayDeleteDialog(false, get(voucher, "deal.dealKey"))}
                        color="primary"
                    >
                        No
                    </Button>
                    <Button onClick={handleDeleteDeal} color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledPageToolbarDiv>
    );
};

export default ConsultingDealToolbar;
